// @generated-imports
import { escapeAttribute, escapeHTML, showToast, slugify, truncate } from './app.js';
import { CONFIG, dom, state } from './core.js';
import { armButtonConfirm } from './datapanel.js';
import { layerClosed, layerOpened, openPromptLayer } from './layers.js';
import { getSetting } from './platform.js';
import { endSession } from './session.js';
import { deleteStatesByPrefix } from './states.js';
// @generated-imports
/* ===========================================================================
   PERFIS LOCAIS (v2.1)
   ---------------------------------------------------------------------------
   Várias pessoas na mesma TV sem misturar favoritos, recentes, tempo de
   jogo, coleções, conquistas e estados do EmuTab. Não há conta nem senha:
   um perfil é um nome + emoji guardados neste navegador.

   Como funciona: o perfil "principal" usa as chaves de sempre (compatível
   com a 2.0). Os demais usam `emutab:p:<id>:<nome>` — applyActiveProfile()
   reescreve as chaves do CONFIG ANTES de qualquer leitura, então todo o
   resto do código continua lendo CONFIG.* sem saber de perfis. Estados do
   EmuTab ganham o prefixo `<id>~` na identidade. Configurações, biblioteca
   de ROMs, jogos offline e BIOS são do aparelho (compartilhados).
   Limitação: saves de bateria (SRAM) do próprio EmulatorJS são por jogo e
   ficam compartilhados entre perfis.
   ======================================================================== */

const MAIN_PROFILE_ID = 'main';
const PROFILE_EMOJIS = ['🎮', '👾', '🚀', '🦊', '🐱', '🐶', '🦖', '⭐', '🌈', '⚡', '🍕', '🎸'];

// chave do CONFIG → sufixo usado no namespace do perfil
const PROFILE_SCOPED_KEYS = {
  localStorageFavoritesKey: 'favorites',
  localStorageRecentKey: 'recent-games',
  localStorageLastGameKey: 'last-game-id',
  localStoragePlayCountsKey: 'play-counts',
  localStoragePlayTimeKey: 'play-time',
  localStorageFiltersKey: 'filters',
  localStorageLastPlayedKey: 'last-played',
  localStorageCollectionsKey: 'collections',
  localStoragePlayLaterKey: 'play-later',
  localStorageAchievementsKey: 'achievements',
  localStorageCountersKey: 'counters',
  localStorageOpenSessionKey: 'open-session',
};

const MAIN_PROFILE_KEYS = Object.fromEntries(Object.keys(PROFILE_SCOPED_KEYS).map((k) => [k, CONFIG[k]]));

function loadProfiles() {
  let list = [];
  try { list = JSON.parse(localStorage.getItem(CONFIG.localStorageProfilesKey) || '[]'); } catch (_) { list = []; }
  if (!Array.isArray(list)) list = [];
  list = list.filter((p) => p && typeof p.id === 'string' && p.id);
  if (!list.some((p) => p.id === MAIN_PROFILE_ID)) list.unshift({ id: MAIN_PROFILE_ID, name: 'Principal', emoji: '🎮' });
  return list;
}

function saveProfiles(list) {
  try { localStorage.setItem(CONFIG.localStorageProfilesKey, JSON.stringify(list)); } catch (_) {}
}

function activeProfileId() {
  const id = localStorage.getItem(CONFIG.localStorageActiveProfileKey) || MAIN_PROFILE_ID;
  return loadProfiles().some((p) => p.id === id) ? id : MAIN_PROFILE_ID;
}

function activeProfile() {
  const id = activeProfileId();
  return loadProfiles().find((p) => p.id === id);
}

function profileKey(profileId, suffix) {
  return `emutab:p:${profileId}:${suffix}`;
}

/** Reescreve as chaves do CONFIG para o perfil ativo. Chamar no início do init. */
function applyActiveProfile() {
  const id = activeProfileId();
  state.profileId = id;
  Object.keys(PROFILE_SCOPED_KEYS).forEach((configKey) => {
    CONFIG[configKey] = id === MAIN_PROFILE_ID ? MAIN_PROFILE_KEYS[configKey] : profileKey(id, PROFILE_SCOPED_KEYS[configKey]);
  });
  return id;
}

/** Prefixo da identidade dos estados do EmuTab para o perfil ativo. */
function profileStatePrefix() {
  const id = state.profileId || MAIN_PROFILE_ID;
  return id === MAIN_PROFILE_ID ? '' : `${id}~`;
}

/** Separa "<perfil>~<gameId>|<core>" → { profile, rest }. */
function splitProfileIdentity(identity) {
  const text = String(identity || '');
  const tilde = text.indexOf('~');
  const bar = text.indexOf('|');
  if (tilde > 0 && (bar < 0 || tilde < bar)) return { profile: text.slice(0, tilde), rest: text.slice(tilde + 1) };
  return { profile: MAIN_PROFILE_ID, rest: text };
}

function isOwnStateKey(key) {
  return splitProfileIdentity(key).profile === (state.profileId || MAIN_PROFILE_ID);
}

function profileName(id) {
  const found = loadProfiles().find((p) => p.id === id);
  return found ? `${found.emoji} ${found.name}` : id;
}

function newProfileId(name) {
  const base = slugify(name).slice(0, 16) || 'perfil';
  let id = base;
  let n = 2;
  const taken = new Set(loadProfiles().map((p) => p.id));
  while (taken.has(id) || id === MAIN_PROFILE_ID) { id = `${base}${n}`; n += 1; }
  return id;
}

async function createProfile(name, emoji) {
  const clean = String(name || '').trim().slice(0, 24);
  if (!clean) return null;
  const list = loadProfiles();
  const profile = { id: newProfileId(clean), name: clean, emoji: emoji || PROFILE_EMOJIS[list.length % PROFILE_EMOJIS.length] };
  list.push(profile);
  saveProfiles(list);
  return profile;
}

function renameProfile(id, name) {
  const list = loadProfiles();
  const found = list.find((p) => p.id === id);
  if (!found || !String(name || '').trim()) return false;
  found.name = String(name).trim().slice(0, 24);
  saveProfiles(list);
  return true;
}

/** Apaga um perfil (nunca o principal): chaves e estados dele. */
async function deleteProfile(id) {
  if (id === MAIN_PROFILE_ID) return false;
  saveProfiles(loadProfiles().filter((p) => p.id !== id));
  const prefix = `emutab:p:${id}:`;
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (key && key.indexOf(prefix) === 0) localStorage.removeItem(key);
  }
  try { await deleteStatesByPrefix(`${id}~`); } catch (_) { /* melhor esforço */ }
  if (localStorage.getItem(CONFIG.localStorageActiveProfileKey) === id) localStorage.removeItem(CONFIG.localStorageActiveProfileKey);
  return true;
}

/** Troca de perfil: encerra a sessão (com autosave) e recarrega a página. */
async function switchProfile(id) {
  if (id === state.profileId) { closeProfilesPanel(); return; }
  await endSession('switch-profile');
  try {
    localStorage.setItem(CONFIG.localStorageActiveProfileKey, id);
    sessionStorage.setItem('emutab:profile-chosen', '1');
  } catch (_) {}
  window.location.reload();
}

/* --- Painel ------------------------------------------------------------- */

function renderProfileChip() {
  if (!dom.profileBtn) return;
  const profile = activeProfile();
  const many = loadProfiles().length > 1;
  dom.profileBtn.innerHTML = `<span aria-hidden="true">${escapeHTML(profile.emoji)}</span>${many ? ` <span class="btn-label">${escapeHTML(truncate(profile.name, 12))}</span>` : ''}`;
  dom.profileBtn.title = `Perfil: ${profile.name} — trocar ou criar perfis`;
  dom.profileBtn.classList.toggle('btn-icon-only', !many);
}

function openProfilesPanel(options = {}) {
  if (!dom.profilesModal) return;
  const list = loadProfiles();
  const current = state.profileId || MAIN_PROFILE_ID;
  dom.profilesTitle.textContent = options.choose ? '👤 Quem vai jogar?' : '👤 Perfis';
  dom.profilesList.innerHTML = list.map((p) => `<div class="backup-row data-row profile-row${p.id === current ? ' is-current' : ''}">
      <button class="btn ${p.id === current ? 'btn-primary' : 'btn-soft'} profile-pick" type="button" data-profile-pick="${escapeAttribute(p.id)}">
        <span aria-hidden="true">${escapeHTML(p.emoji)}</span> ${escapeHTML(p.name)}${p.id === current ? ' <small>(ativo)</small>' : ''}
      </button>
      ${options.choose ? '' : `<span class="data-actions">
        <button class="mini-btn" type="button" data-profile-rename="${escapeAttribute(p.id)}">Renomear</button>
        ${p.id === MAIN_PROFILE_ID ? '' : `<button class="mini-btn mini-btn-danger" type="button" data-profile-delete="${escapeAttribute(p.id)}">Apagar</button>`}
      </span>`}
    </div>`).join('')
    + `<div class="backup-row data-row"><span class="data-info"><small>Favoritos, recentes, tempo, coleções, conquistas e estados são de cada perfil. Configurações, ROMs locais e jogos offline são do aparelho.</small></span>
      <span class="data-actions"><button class="mini-btn" type="button" data-profile-new="1">➕ Novo perfil</button></span></div>`;
  dom.profilesModal.classList.remove('is-hidden');
  layerOpened('profiles', dom.profilesList.querySelector('.is-current .profile-pick') || dom.profilesList.querySelector('.profile-pick'));
}

function closeProfilesPanel() {
  if (!dom.profilesModal) return;
  dom.profilesModal.classList.add('is-hidden');
  layerClosed('profiles');
}

async function handleProfilesClick(event) {
  const pick = event.target.closest('[data-profile-pick]');
  if (pick) { switchProfile(pick.dataset.profilePick); return; }
  const rename = event.target.closest('[data-profile-rename]');
  if (rename) {
    const id = rename.dataset.profileRename;
    const current = loadProfiles().find((p) => p.id === id);
    const name = await openPromptLayer({ title: 'Renomear perfil', label: 'Nome', value: current ? current.name : '' });
    if (name && renameProfile(id, name)) { renderProfileChip(); openProfilesPanel(); }
    return;
  }
  const del = event.target.closest('[data-profile-delete]');
  if (del) {
    if (!armButtonConfirm(del)) return;
    await deleteProfile(del.dataset.profileDelete);
    openProfilesPanel();
    renderProfileChip();
    showToast('Perfil apagado com os dados dele.');
    return;
  }
  if (event.target.closest('[data-profile-new]')) {
    const name = await openPromptLayer({ title: 'Novo perfil', label: 'Nome de quem vai jogar', placeholder: 'Ex.: Ana' });
    if (!name) { openProfilesPanel(); return; }
    const profile = await createProfile(name);
    if (profile) {
      showToast(`Perfil ${profile.emoji} ${profile.name} criado.`, { action: { label: 'Usar agora', handler: () => switchProfile(profile.id) } });
      renderProfileChip();
    }
    openProfilesPanel();
  }
}

/** No boot: com mais de um perfil e a opção ligada, pergunta quem joga. */
function maybeAskProfile() {
  if (loadProfiles().length < 2 || !getSetting('askProfileOnStart')) return false;
  let chosen = false;
  try { chosen = sessionStorage.getItem('emutab:profile-chosen') === '1'; } catch (_) {}
  if (chosen) return false;
  try { sessionStorage.setItem('emutab:profile-chosen', '1'); } catch (_) {}
  openProfilesPanel({ choose: true });
  return true;
}

// @generated-exports
export { MAIN_PROFILE_ID, MAIN_PROFILE_KEYS, PROFILE_EMOJIS, PROFILE_SCOPED_KEYS, activeProfile, activeProfileId, applyActiveProfile, closeProfilesPanel, createProfile, deleteProfile, handleProfilesClick, isOwnStateKey, loadProfiles, maybeAskProfile, newProfileId, openProfilesPanel, profileKey, profileName, profileStatePrefix, renameProfile, renderProfileChip, saveProfiles, splitProfileIdentity, switchProfile };
// @generated-exports
